/**
 * Triage Queue Component
 * 
 * Always-visible panel showing top 20 unresolved critical violations.
 */

import React from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { DarkPanel } from '@/components/ui/DarkPanel';
import { Badge } from '@/components/ui/badge';
import { Violation } from '@/hooks/useCompileGateViewModel';

interface TriageQueueProps {
  violations: Violation[];
  reviewed: Set<string>;
  getViolationId: (v: Violation, index: number) => string;
  onViolationClick: (violationId: string) => void;
}

export const TriageQueue: React.FC<TriageQueueProps> = ({
  violations,
  reviewed,
  getViolationId,
  onViolationClick
}) => {
  // Get top 20 unresolved critical violations
  const topCriticals = violations
    .filter((v, idx) => {
      const id = getViolationId(v, idx);
      return v.severity.toLowerCase() === 'critical' && !reviewed.has(id);
    })
    .slice(0, 20);

  if (topCriticals.length === 0) {
    return null;
  }

  return (
    <DarkPanel className="p-4 h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <h3 className="font-semibold">Triage Queue</h3>
        </div>
        <Badge variant="destructive">{topCriticals.length}</Badge>
      </div>

      <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
        {topCriticals.map((violation, idx) => {
          const violationId = getViolationId(violation, idx);
          return (
            <button
              key={violationId}
              onClick={() => onViolationClick(violationId)}
              className="w-full text-left p-3 bg-red-500/10 border border-red-500/20 rounded hover:bg-red-500/20 transition-colors"
            >
              <div className="flex items-start justify-between mb-1">
                <div className="flex-1 min-w-0">
                  {violation.activity_id && (
                    <div className="text-xs text-gray-300 mb-1 truncate">
                      {violation.activity_id}
                    </div>
                  )}
                  <div className="text-sm font-medium text-gray-200 truncate">
                    {violation.activity_name || violation.rule_name}
                  </div>
                </div>
                <ExternalLink className="h-4 w-4 text-gray-400 ml-2 shrink-0" />
              </div>
              <div className="text-xs text-gray-400 truncate">
                {violation.message}
              </div>
            </button>
          );
        })}
      </div>
    </DarkPanel>
  );
};
